import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CursorClick, UserPlus, CurrencyDollar, Wallet, Copy, Check, Tag, CreditCard } from "@phosphor-icons/react";
import Logo from "../components/Logo";
import { PartnerLeaderboard } from "../components/PartnerLeaderboard";
import client from "../lib/api";

const money = (c) => `$${(c / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

function Stat({ icon: Icon, label, value, tone = "text-white", testid }) {
  return (
    <div className="card-surface p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-widest font-bold"><Icon size={14} /> {label}</div>
      <div className={`font-display text-3xl sm:text-4xl font-bold mt-2 ${tone}`}>{value}</div>
    </div>
  );
}

function CopyRow({ label, value, testid }) {
  const [ok, setOk] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(value); setOk(true); setTimeout(() => setOk(false), 1500); } catch { /* ignore */ } };
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm" data-testid={testid}>
      <span className="text-neutral-500 w-24">{label}</span>
      <code className="font-mono text-[#FF5A1F] bg-white/5 border border-white/10 rounded px-2 py-0.5 truncate max-w-[70vw]">{value}</code>
      <button type="button" onClick={copy} className="text-neutral-400 hover:text-white" aria-label={`Copy ${label}`} data-testid={`${testid}-copy`}>{ok ? <Check size={14} /> : <Copy size={14} />}</button>
    </div>
  );
}

export default function PartnerStatsPage() {
  const { code } = useParams();
  const [sp] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    client.get(`/partners/${encodeURIComponent(code)}/stats`, { params: { t: sp.get("t") || "" } })
      .then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || "This stats link isn't valid"));
  }, [code, sp]);

  return (
    <div className="min-h-screen bg-neutral-950 text-white noise-bg" data-testid="partner-stats-page">
      <header className="max-w-4xl mx-auto px-4 sm:px-8 py-6 flex items-center justify-between">
        <Link to="/"><Logo /></Link>
        <span className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Partner dashboard</span>
      </header>
      <main className="max-w-4xl mx-auto px-4 sm:px-8 pb-16">
        {err && <div className="card-surface p-8 text-neutral-400" data-testid="partner-stats-error">{err}</div>}
        {data && (
          <>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="partner-stats-name">{data.name}</h1>
            <p className="text-neutral-400 mt-1 text-sm">{data.type} · you earn <b className="text-white">{money(data.payout_cents)}</b> for every customer from your link who starts paying (7-day free trial first, card required){data.promo_pct ? ` · your audience gets ${data.promo_pct}% off their first month` : ""}.</p>
            <div className="mt-6 space-y-2">
              <CopyRow label="Your link" value={data.link} testid="partner-stats-link" />
              {data.promo_code && <CopyRow label="Promo code" value={data.promo_code} testid="partner-stats-promo" />}
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-8">
              <Stat icon={CursorClick} label="Clicks" value={data.clicks} testid="partner-stat-clicks" />
              <Stat icon={UserPlus} label="Signups" value={data.signups} testid="partner-stat-signups" />
              <Stat icon={CreditCard} label="Cards on file" value={data.cards_on_file ?? 0} tone="text-sky-300" testid="partner-stat-cards" />
              <Stat icon={CurrencyDollar} label="Paying" value={data.paid} tone="text-emerald-300" testid="partner-stat-paid" />
              <Stat icon={Wallet} label="Owed to you" value={money(data.owed_cents)} tone="text-[#FF5A1F]" testid="partner-stat-owed" />
            </div>
            <div className="card-surface p-5 mt-6 text-sm border border-[#FF5A1F]/30" data-testid="partner-stats-month">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="text-neutral-400">This month:</span>
                <b className="text-white" data-testid="partner-stats-month-paid">{data.paid_this_month} paying</b>
                {data.bonus_this_month_cents > 0 ? (
                  <span className="text-[#FF5A1F] font-bold" data-testid="partner-stats-month-bonus">+{money(data.bonus_this_month_cents)} bonus unlocked</span>
                ) : data.next_tier ? (
                  <span className="text-neutral-400" data-testid="partner-stats-next-tier">{data.next_tier.to_go} more paying customer{data.next_tier.to_go === 1 ? "" : "s"} this month unlocks a <b className="text-[#FF5A1F]">{money(data.next_tier.bonus_cents)}</b> bonus</span>
                ) : null}
                {data.bonus_this_month_cents > 0 && data.next_tier && <span className="text-neutral-500 text-xs">next: {data.next_tier.at} paying → +{money(data.next_tier.bonus_cents)}</span>}
                {data.bonus_total_cents > 0 && <span className="ml-auto text-xs text-neutral-500">bonuses earned all-time: {money(data.bonus_total_cents)}</span>}
              </div>
            </div>
            <div className="mt-4"><PartnerLeaderboard highlightCode={data.code} /></div>
            <div className="card-surface p-5 mt-4 text-sm" data-testid="partner-stats-payouts">
              <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-widest font-bold mb-3"><Tag size={14} /> Payouts · {money(data.paid_out_cents)} paid so far</div>
              {data.payouts.length === 0 ? <div className="text-neutral-500">No payouts yet — paid monthly once you have a paying customer.</div> : (
                <ul className="space-y-1.5">{data.payouts.slice().reverse().map((p, i) => (
                  <li key={i} className="flex justify-between gap-3 text-neutral-300"><span>{fmt(p.at)}{p.note ? ` · ${p.note}` : ""}</span><b className="text-emerald-300">{money(p.amount_cents)}</b></li>
                ))}</ul>
              )}
            </div>
            <p className="text-xs text-neutral-600 mt-6">Counts update live. A "paying" customer is someone who signed up from your link and is on an active paid plan. "Owed" includes per-customer payouts plus any monthly bonuses, minus what's already been paid out.</p>
          </>
        )}
      </main>
    </div>
  );
}
